import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { restrictedCaseApi } from '../../api/restricted-cases';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Send } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  investigation: 'Investigation', decision_pending: 'Decision Pending', closed: 'Closed', cancelled: 'Cancelled',
};

/** Section 46 - staff see only what they're entitled to see: reference, status, and any request for more information. Never investigation notes or respondent detail. */
export function MyRestrictedCasesPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const mineQuery = useQuery({ queryKey: ['my-restricted-cases'], queryFn: () => restrictedCaseApi.mine() });
  const submitMutation = useMutation({
    mutationFn: (id: number) => restrictedCaseApi.submit(id),
    onSuccess: () => { showSuccess('Submitted.'); queryClient.invalidateQueries({ queryKey: ['my-restricted-cases'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <PageHeader title="My Reported Cases" description="This information is confidential. To report a new concern, contact HR directly." />

      <section className="card">
        {mineQuery.data && (
          <DataTable
            rows={mineQuery.data.data}
            rowKey={(r) => r.id!}
            emptyMessage="No cases reported."
            columns={[
              { header: 'Reference', render: (r) => r.case_reference },
              { header: 'Status', render: (r) => <StatusBadge status={r.status!} label={STATUS_LABELS[r.status!]} /> },
              { header: '', render: (r) => r.status === 'draft' && <IconActionButton icon={Send} label="Submit" onClick={() => submitMutation.mutate(r.id!)} /> },
            ]}
          />
        )}
        {mineQuery.data?.data.filter((r) => r.status === 'returned').map((r) => (
          <ReturnedBanner key={r.id} returnComment={r.return_comment ?? null} resubmit={() => restrictedCaseApi.resubmit(r.id!)} queryKeysToInvalidate={[['my-restricted-cases']]} />
        ))}
      </section>
    </div>
  );
}
